import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    inject,
    input,
    InputSignal,
    OnDestroy,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { IqMcGaugeQueryLtComponent } from '@dat/iq-mc-gauge-query';
import { IqMcGaugeMciComponent } from '../iq-mc-gauge-mci/iq-mc-gauge-mci.component';
import {
    EquipmentTypes,
    SetMarketDetailsLabelPayload,
    SetMarketDetailsLabelPayloadSchema,
    UpdateMcQueryControlsPayload,
    UpdateMcQueryControlsPayloadSchema,
} from '../models';
import { startCase } from 'lodash';

@Component({
    selector: 'iq-mc-gauge-section',
    standalone: true,
    imports: [
        CommonModule,
        DatiqTemplateModule,
        MatIconModule,
        IqMcGaugeMciComponent,
        IqMcGaugeQueryLtComponent,
    ],
    templateUrl: './iq-mc-gauge-section.component.html',
    styleUrl: './iq-mc-gauge-section.component.scss',
})
export class IqMcGaugeSectionComponent implements OnDestroy {
    id: InputSignal<string> = input('');
    mciGaugeId: InputSignal<string> = input('');
    ltGaugeId: InputSignal<string> = input('');

    private _payload: WritableSignal<UpdateMcQueryControlsPayload> = signal({
        direction: 'OUT',
        displayName: '',
        geoCode: '',
        equipmentType: EquipmentTypes.Van,
        timeframe: 'CURRENT',
        geoType: 'MKT',
    });

    private _marketDetailsLabel: WritableSignal<SetMarketDetailsLabelPayload> =
        signal({
            name: '',
        });

    isDisplayNameAvailable: Signal<boolean> = computed(() => {
        return !!this._payload().displayName || !!this._payload().geoCode;
    });

    guageSectionLabel: Signal<string> = computed(() => {
        const direction = this._payload().direction;
        const directionLabel = `${direction}bound Capacity`;

        const displayName = this._payload().displayName;
        const label = this._marketDetailsLabel().name;
        if (
            displayName &&
            displayName.length > 0 &&
            label &&
            label.length > 0
        ) {
            return `${directionLabel} - ${label}`;
        }
        return directionLabel;
    });

    guageSectionInfo: Signal<string> = computed(() => {
        const equipmentType: string = startCase(
            this._payload().equipmentType.toLowerCase(),
        );
        const timeframe: string = startCase(
            this._payload().timeframe.toLowerCase(),
        );
        const geoType: string = startCase(
            this._payload().geoType.toLowerCase(),
        );
        return `${equipmentType} | ${timeframe} | ${geoType}`;
    });

    private _deregisterMap: Record<string, IqEventBusBrokerHandler> = {};

    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    constructor() {
        this._deregisterMap = this._bus.registerMap({
            updateMcQueryControls: {
                callback: (payload: UpdateMcQueryControlsPayload) => {
                    const test: any =
                        UpdateMcQueryControlsPayloadSchema.safeParse(payload);

                    if (!test.success) {
                        console.warn('UpdateMcQueryControl Invalid Payload');
                        console.log(test?.error?.issues);
                        return;
                    }

                    this._payload.set(payload);
                },
            },
            setMarketDetailsLabel: {
                callback: (payload: SetMarketDetailsLabelPayload) => {
                    const test: any =
                        SetMarketDetailsLabelPayloadSchema.safeParse(payload);

                    if (!test.success) {
                        console.warn('setMarketDetailsLabel Invalid Payload');
                        console.log(test?.error?.issues);
                        return;
                    }

                    this._marketDetailsLabel.set(payload);
                },
            },
        });
    }

    ngOnDestroy(): void {
        this._bus.unregisterMap(this._deregisterMap);
    }
}
